import React, { Ref } from "react";
import ReactSelect, { GroupBase } from "react-select";
import CreatableSelect from "react-select/creatable";
import { InfoMenu } from "@src/components/elements/InfoMenu";
import { generateSelectStyles } from "@src/components/elements/Select/generateSelectStyles";
import { SelectProps } from "@src/components/elements/Select/SelectProps";
import { SelectStyledRoot } from "@src/components/elements/Select/SelectStyledRoot";
import { SelectOption } from "@src/components/elements/Select/SelectOption";
import ReactSelectDecl from "react-select/dist/declarations/src/Select";

type Props = SelectProps<false> & {
  inputRef?: Ref<ReactSelectDecl<SelectOption, false, GroupBase<SelectOption>>>;
};

export const Select: React.FC<Props> = React.forwardRef(
  (
    {
      label,
      placeholder,
      options,
      value,
      onChange,
      isCreatable,
      onCreateOption,
      isLoading,
      className,
      error,
      required,
      isDisabled,
      formatOptionLabel,
      size,
      tooltipText,
      inputRef,
      menuPortalTarget = null,
      isClearable = true,
      horizontalLabel = false,
      disableError = false,
      isOptionDisabled,
      labelType = "normal",
      minWidth,
      maxWidth,
      components,
    },
    ref: Ref<ReactSelectDecl<SelectOption, false, GroupBase<SelectOption>>>,
  ) => {
    const [isFocused, setIsFocused] = React.useState(false);
    const labelShouldFloat =
      labelType === "floating" && (isFocused || !!value?.value);

    return (
      <SelectStyledRoot
        labelShouldFloat={labelShouldFloat}
        horizontalLabel={horizontalLabel}
        className={`${error ? "field-error" : ""} ${className ?? ""}`}
        labelType={labelType}
        minWidth={minWidth}
        maxWidth={maxWidth}
      >
        {label && labelType === "normal" && (
          <div className="label-wrap">
            <label htmlFor={label}>
              {label} {required && <span className="error">*</span>}{" "}
            </label>
            {tooltipText && <InfoMenu title={<span>{tooltipText}</span>} />}
          </div>
        )}
        <div className="input-container">
          {isCreatable ? (
            <CreatableSelect<SelectOption, false>
              className="select-input"
              ref={inputRef || ref}
              isClearable={isClearable}
              value={value?.value === "" ? null : value}
              isDisabled={isDisabled}
              isLoading={isLoading}
              placeholder={labelShouldFloat ? "" : placeholder}
              styles={generateSelectStyles(error, size, labelType)}
              menuShouldBlockScroll={true}
              options={options}
              onChange={onChange}
              onCreateOption={onCreateOption}
              menuPortalTarget={menuPortalTarget}
              isOptionDisabled={isOptionDisabled}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              components={components}
              menuPlacement="auto"
            />
          ) : (
            <ReactSelect
              ref={inputRef || ref}
              className="select-input"
              isClearable={isClearable}
              value={value?.value === "" ? null : value}
              formatOptionLabel={formatOptionLabel}
              isDisabled={isDisabled}
              isLoading={isLoading}
              placeholder={
                labelType === "normal"
                  ? placeholder
                  : labelShouldFloat
                  ? ""
                  : placeholder
              }
              menuShouldBlockScroll={true}
              styles={generateSelectStyles(error, size)}
              options={options}
              onChange={onChange}
              menuPortalTarget={menuPortalTarget}
              isOptionDisabled={isOptionDisabled}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              components={components}
              menuPlacement="auto"
            />
          )}
          {labelType === "floating" ? (
            <div className="label-wrap">
              <label className="floating-label" htmlFor={label}>
                {label}
                {required && <span className="error">*</span>}
              </label>
            </div>
          ) : null}
        </div>

        {!disableError && <div className="error">{error}&nbsp;</div>}
      </SelectStyledRoot>
    );
  },
);

Select.displayName = "Select";

export const MemoizedSelect = React.memo(Select);
